/**
 * Presentational chrome for the LEVEL EDITOR — the Toolbar, Palette, Inspector, and Validation panel.
 * These are dumb views: they render the working {@link LevelDocument} + selection and call back to the
 * page (which owns the document + all mutations). Styling is inline (the app's convention) in a calm
 * dark palette consistent with the HUD; labels are dual-coded (icon + text) per the accessibility floor.
 */
import { Fragment, useRef, useState, type CSSProperties, type JSX } from 'react';
import {
  ANIMATION_STATES,
  PRESENCE_FLAVORS,
  WORLD_THEMES,
  WORLD_THEME_IDS,
  type Animation,
  type InteractiveObject,
  type LevelDocument,
  type LevelIssue,
  type PresenceFlavor,
  type Seat,
  type WorldDoor,
  type WorldThemeId,
  type WorldZone,
} from '@duck/contracts';
import {
  OBJECT_CATALOG,
  SEAT_CATALOG,
  findItem,
  type PlacementArm,
  type SelectionRef,
} from './editor-doc';
import { DECOR_CATALOG } from '../office/render/decor-catalog';
import { sceneryCatalog } from '../office/render/scenery';
import type { DecorItem } from '@duck/contracts';

const PANEL: CSSProperties = {
  background: 'rgba(13,18,34,0.92)',
  border: '1px solid rgba(120,140,190,0.25)',
  borderRadius: 12,
  padding: 12,
  color: '#eef1ff',
  font: '13px ui-sans-serif, system-ui, sans-serif',
};
const HEADING: CSSProperties = { margin: '0 0 8px', fontSize: 12, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', opacity: 0.75 };
// NOTE: use border/font LONGHAND (not the `border`/`font` shorthands) — variants below override
// borderColor + fontWeight, and React warns if a shorthand and its longhand mix on one element.
const BTN: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'rgba(40,52,86,0.9)',
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: 'rgba(120,140,190,0.3)',
  borderRadius: 8,
  color: '#eef1ff',
  padding: '6px 10px',
  cursor: 'pointer',
  fontSize: 13,
  fontFamily: 'ui-sans-serif, system-ui, sans-serif',
  fontWeight: 400,
};
const BTN_ACTIVE: CSSProperties = { ...BTN, background: '#ffd24a', color: '#1a1408', borderColor: '#ffd24a', fontWeight: 700 };
const INPUT: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  background: 'rgba(8,12,24,0.8)',
  border: '1px solid rgba(120,140,190,0.3)',
  borderRadius: 6,
  color: '#eef1ff',
  padding: '5px 7px',
  font: 'inherit',
};
const ROW: CSSProperties = { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 };

// ── Toolbar ───────────────────────────────────────────────────────────────────────────────────────

export function Toolbar(props: {
  doc: LevelDocument;
  gridOn: boolean;
  baked: boolean;
  hasErrors: boolean;
  issueCount: number;
  canUndo: boolean;
  canRedo: boolean;
  onNewBlank: () => void;
  onNewOffice: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onFit: () => void;
  onToggleGrid: () => void;
  onToggleBake: () => void;
  onThemeChange: (id: WorldThemeId) => void;
  onExport: () => void;
  onImport: (json: string) => void;
}): JSX.Element {
  const fileRef = useRef<HTMLInputElement>(null);
  return (
    <div style={{ ...PANEL, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
      <strong style={{ fontSize: 14, marginRight: 4 }}>🦆 Level Editor</strong>
      <button style={BTN} onClick={props.onNewBlank} title="Start a brand-new blank map from scratch">📄 New</button>
      <button style={BTN} onClick={props.onNewOffice} title="Load the office layout (reset)">🏢 Office</button>
      <button style={{ ...BTN, opacity: props.canUndo ? 1 : 0.4 }} onClick={props.onUndo} title="Undo (Ctrl/Cmd+Z)">↶</button>
      <button style={{ ...BTN, opacity: props.canRedo ? 1 : 0.4 }} onClick={props.onRedo} title="Redo (Ctrl/Cmd+Shift+Z)">↷</button>
      <button style={BTN} onClick={props.onExport} title="Download + copy this level as JSON (Ctrl/Cmd+S)">⬇️ Export</button>
      <button style={BTN} onClick={() => fileRef.current?.click()} title="Load a level JSON file">⬆️ Import</button>
      <input
        ref={fileRef}
        type="file"
        accept="application/json,.json"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          void file.text().then(props.onImport);
          e.target.value = '';
        }}
      />
      <button style={props.gridOn ? BTN_ACTIVE : BTN} onClick={props.onToggleGrid} title="Toggle the snap grid">▦ Grid</button>
      <button style={BTN} onClick={props.onFit} title="Fit the whole world in view">⤢ Fit</button>
      <button
        style={props.baked ? BTN_ACTIVE : BTN}
        onClick={props.onToggleBake}
        title="Bake the finished map to ONE optimized backdrop sprite (1 draw call). Toggle off to edit."
      >
        ⚡ {props.baked ? 'Baked' : 'Bake'}
      </button>
      <label style={{ ...ROW, marginBottom: 0, gap: 4 }}>
        🎨
        <select
          style={{ ...INPUT, width: 'auto' }}
          value={props.doc.themeId}
          onChange={(e) => props.onThemeChange(e.target.value as WorldThemeId)}
        >
          {WORLD_THEME_IDS.map((id) => (
            <option key={id} value={id}>
              {WORLD_THEMES[id].name}
            </option>
          ))}
        </select>
      </label>
      <span
        style={{ marginLeft: 'auto', fontWeight: 700, color: props.hasErrors ? '#ff8f8f' : '#9be29b' }}
        data-testid="editor-status"
      >
        {props.hasErrors ? `✖ ${props.issueCount} issue${props.issueCount === 1 ? '' : 's'}` : '✓ Valid level'}
      </span>
    </div>
  );
}

// ── Palette ───────────────────────────────────────────────────────────────────────────────────────

type PaletteTab = 'rooms' | 'objects' | 'seats' | 'decor';

export function Palette(props: {
  placement: PlacementArm | null;
  onArm: (arm: PlacementArm | null) => void;
}): JSX.Element {
  const [tab, setTab] = useState<PaletteTab>('decor');
  const isArmed = (type: PlacementArm['type'], kind?: string): boolean =>
    props.placement?.type === type && props.placement?.kind === kind;

  return (
    <div style={PANEL}>
      <h3 style={HEADING}>Palette</h3>
      <div style={{ ...ROW, marginBottom: 10 }}>
        <button style={props.placement === null ? BTN_ACTIVE : BTN} onClick={() => props.onArm(null)}>↖ Select</button>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
        {(['decor', 'objects', 'seats', 'rooms'] as const).map((t) => (
          <button key={t} style={tab === t ? BTN_ACTIVE : BTN} onClick={() => setTab(t)}>
            {t === 'rooms' ? '▢ Rooms' : t === 'objects' ? '☕ Objects' : t === 'seats' ? '🪑 Seats' : '🛋️ Furniture'}
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 200, overflowY: 'auto' }}>
        {tab === 'rooms' && (
          <button
            style={isArmed('zone') ? BTN_ACTIVE : BTN}
            onClick={() => props.onArm({ type: 'zone' })}
          >
            ▢ Room
          </button>
        )}
        {tab === 'objects' &&
          OBJECT_CATALOG.map((o) => (
            <button
              key={o.kind}
              style={isArmed('object', o.kind) ? BTN_ACTIVE : BTN}
              onClick={() => props.onArm({ type: 'object', kind: o.kind })}
            >
              {o.icon} {o.label}
            </button>
          ))}
        {tab === 'seats' &&
          SEAT_CATALOG.map((s) => (
            <button
              key={s.kind}
              style={isArmed('seat', s.kind) ? BTN_ACTIVE : BTN}
              onClick={() => props.onArm({ type: 'seat', kind: s.kind })}
            >
              {s.icon} {s.label}
            </button>
          ))}
        {tab === 'decor' &&
          [...DECOR_CATALOG, ...sceneryCatalog()].map((d) => (
            <button
              key={d.kind}
              style={isArmed('decor', d.kind) ? BTN_ACTIVE : BTN}
              onClick={() => props.onArm({ type: 'decor', kind: d.kind })}
            >
              {d.icon} {d.label}
            </button>
          ))}
      </div>
      <p style={{ opacity: 0.6, fontSize: 12, marginTop: 10, marginBottom: 0 }}>
        {props.placement ? 'Click on the map to place it.' : 'Click any furniture / object on the map to select + move it.'}
      </p>
    </div>
  );
}

// ── Inspector ─────────────────────────────────────────────────────────────────────────────────────

function NumberField(props: { label: string; value: number; onChange: (v: number) => void }): JSX.Element {
  return (
    <label style={ROW}>
      <span style={{ width: 70, opacity: 0.8 }}>{props.label}</span>
      <input
        style={INPUT}
        type="number"
        value={Number.isFinite(props.value) ? props.value : 0}
        onChange={(e) => {
          const v = parseFloat(e.target.value);
          if (Number.isFinite(v)) props.onChange(v);
        }}
      />
    </label>
  );
}

/** A ↺ ↻ rotate row (15° steps, layout-friendly) shown for every rotatable element. */
function RotateRow(props: { onRotate: (delta: number) => void }): JSX.Element {
  const step = Math.PI / 12;
  return (
    <div style={{ ...ROW, gap: 6 }}>
      <span style={{ width: 70, opacity: 0.8 }}>Rotate</span>
      <button style={BTN} onClick={() => props.onRotate(-step)} title="Rotate left 15° (Shift+R)">↺</button>
      <button style={BTN} onClick={() => props.onRotate(step)} title="Rotate right 15° (R)">↻</button>
      <span style={{ opacity: 0.5, fontSize: 11 }}>15° · key R</span>
    </div>
  );
}

export function Inspector(props: {
  doc: LevelDocument;
  sel: SelectionRef | null;
  onUpdate: (patch: Record<string, unknown>) => void;
  onAddDoor: (side: WorldDoor['side']) => void;
  onRotate: (delta: number) => void;
  onDelete: () => void;
}): JSX.Element {
  const item = findItem(props.doc, props.sel);
  if (!props.sel || !item) {
    return (
      <div style={PANEL}>
        <h3 style={HEADING}>Inspector</h3>
        <p style={{ opacity: 0.6, margin: 0 }}>Nothing selected.</p>
      </div>
    );
  }

  return (
    <div style={PANEL}>
      <h3 style={HEADING}>Inspector · {props.sel.type}</h3>
      <div style={{ ...ROW, opacity: 0.7, fontSize: 12 }}>id: {props.sel.id}</div>

      {props.sel.type === 'zone' && (() => {
        const z = item as WorldZone;
        return (
          <>
            <label style={ROW}>
              <span style={{ width: 70, opacity: 0.8 }}>Name</span>
              <input style={INPUT} value={z.name} onChange={(e) => props.onUpdate({ name: e.target.value })} />
            </label>
            <NumberField label="X" value={z.x} onChange={(v) => props.onUpdate({ x: v })} />
            <NumberField label="Y" value={z.y} onChange={(v) => props.onUpdate({ y: v })} />
            <NumberField label="Width" value={z.width} onChange={(v) => props.onUpdate({ width: Math.max(40, v) })} />
            <NumberField label="Height" value={z.height} onChange={(v) => props.onUpdate({ height: Math.max(40, v) })} />
            <label style={{ ...ROW, gap: 6 }}>
              <input type="checkbox" checked={z.isolated} onChange={(e) => props.onUpdate({ isolated: e.target.checked })} />
              <span style={{ opacity: 0.8 }}>Isolated (sealed nook)</span>
            </label>
            <div style={{ ...ROW, flexWrap: 'wrap' }}>
              <span style={{ width: 70, opacity: 0.8 }}>Add door</span>
              {(['N', 'S', 'E', 'W'] as const).map((side) => (
                <button key={side} style={BTN} onClick={() => props.onAddDoor(side)}>
                  ＋{side}
                </button>
              ))}
            </div>
          </>
        );
      })()}

      {props.sel.type === 'object' && (() => {
        const o = item as InteractiveObject;
        return (
          <>
            <label style={ROW}>
              <span style={{ width: 70, opacity: 0.8 }}>Label</span>
              <input style={INPUT} value={o.label} onChange={(e) => props.onUpdate({ label: e.target.value })} />
            </label>
            <label style={ROW}>
              <span style={{ width: 70, opacity: 0.8 }}>Kind</span>
              <select style={INPUT} value={o.kind} onChange={(e) => props.onUpdate({ kind: e.target.value })}>
                {OBJECT_CATALOG.map((c) => (
                  <option key={c.kind} value={c.kind}>{c.label}</option>
                ))}
              </select>
            </label>
            <NumberField label="X" value={o.x} onChange={(v) => props.onUpdate({ x: v })} />
            <NumberField label="Y" value={o.y} onChange={(v) => props.onUpdate({ y: v })} />
            <NumberField
              label="Rotation°"
              value={Math.round(((o.rotation ?? 0) * 180) / Math.PI)}
              onChange={(v) => props.onUpdate({ rotation: (v * Math.PI) / 180 })}
            />
            <RotateRow onRotate={props.onRotate} />
            <label style={ROW}>
              <span style={{ width: 70, opacity: 0.8 }}>Presence</span>
              <select
                style={INPUT}
                value={o.effect.presence ?? 'none'}
                onChange={(e) => props.onUpdate({ effect: { ...o.effect, presence: e.target.value as PresenceFlavor } })}
              >
                {PRESENCE_FLAVORS.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </label>
            <label style={ROW}>
              <span style={{ width: 70, opacity: 0.8 }}>Animation</span>
              <select
                style={INPUT}
                value={o.effect.animation ?? 'idle'}
                onChange={(e) => props.onUpdate({ effect: { ...o.effect, animation: e.target.value as Animation } })}
              >
                {ANIMATION_STATES.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </label>
          </>
        );
      })()}

      {props.sel.type === 'seat' && (() => {
        const s = item as Seat;
        return (
          <>
            <label style={ROW}>
              <span style={{ width: 70, opacity: 0.8 }}>Label</span>
              <input style={INPUT} value={s.label} onChange={(e) => props.onUpdate({ label: e.target.value })} />
            </label>
            <label style={ROW}>
              <span style={{ width: 70, opacity: 0.8 }}>Kind</span>
              <select style={INPUT} value={s.kind} onChange={(e) => props.onUpdate({ kind: e.target.value })}>
                {SEAT_CATALOG.map((c) => (
                  <option key={c.kind} value={c.kind}>{c.label}</option>
                ))}
              </select>
            </label>
            <NumberField label="X" value={s.x} onChange={(v) => props.onUpdate({ x: v })} />
            <NumberField label="Y" value={s.y} onChange={(v) => props.onUpdate({ y: v })} />
            <NumberField label="Facing" value={s.facing} onChange={(v) => props.onUpdate({ facing: v })} />
            <RotateRow onRotate={props.onRotate} />
          </>
        );
      })()}

      {props.sel.type === 'decor' && (() => {
        const d = item as DecorItem;
        const deg = Math.round(((d.rotation ?? 0) * 180) / Math.PI);
        return (
          <>
            <label style={ROW}>
              <span style={{ width: 70, opacity: 0.8 }}>Kind</span>
              <select style={INPUT} value={d.kind} onChange={(e) => props.onUpdate({ kind: e.target.value })}>
                {DECOR_CATALOG.map((c) => (
                  <option key={c.kind} value={c.kind}>{c.label}</option>
                ))}
              </select>
            </label>
            <NumberField label="X" value={d.x} onChange={(v) => props.onUpdate({ x: v })} />
            <NumberField label="Y" value={d.y} onChange={(v) => props.onUpdate({ y: v })} />
            <NumberField label="Rotation°" value={deg} onChange={(v) => props.onUpdate({ rotation: (v * Math.PI) / 180 })} />
            <RotateRow onRotate={props.onRotate} />
            <NumberField label="Scale" value={d.scale ?? 1} onChange={(v) => props.onUpdate({ scale: Math.max(0.2, v) })} />
            {(d.kind === 'rug' || d.kind === 'long-desk') && (
              <NumberField label="Width" value={d.w ?? 120} onChange={(v) => props.onUpdate({ w: Math.max(20, v) })} />
            )}
            {d.kind === 'rug' && (
              <NumberField label="Height" value={d.h ?? 80} onChange={(v) => props.onUpdate({ h: Math.max(20, v) })} />
            )}
          </>
        );
      })()}

      {props.sel.type === 'door' && (() => {
        const d = item as WorldDoor;
        return (
          <>
            <label style={ROW}>
              <span style={{ width: 70, opacity: 0.8 }}>Side</span>
              <select style={INPUT} value={d.side} onChange={(e) => props.onUpdate({ side: e.target.value })}>
                {(['N', 'S', 'E', 'W'] as const).map((side) => (
                  <option key={side} value={side}>{side}</option>
                ))}
              </select>
            </label>
            <NumberField label="Pos" value={d.pos} onChange={(v) => props.onUpdate({ pos: v })} />
            <NumberField label="Width" value={d.width} onChange={(v) => props.onUpdate({ width: Math.max(40, v) })} />
            <div style={{ ...ROW, opacity: 0.7, fontSize: 12 }}>room: {d.zoneId}</div>
          </>
        );
      })()}

      <button
        style={{ ...BTN, marginTop: 8, background: 'rgba(120,30,30,0.7)', borderColor: '#ff8f8f' }}
        onClick={props.onDelete}
      >
        🗑 Delete
      </button>
    </div>
  );
}

// ── Hotkey legend ─────────────────────────────────────────────────────────────────────────────────

const KBD: CSSProperties = {
  background: 'rgba(8,12,24,0.8)',
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: 'rgba(120,140,190,0.3)',
  borderRadius: 5,
  padding: '1px 6px',
  fontSize: 11,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  whiteSpace: 'nowrap',
};

export function HotkeyLegend(): JSX.Element {
  const rows: ReadonlyArray<readonly [string, string]> = [
    ['Click · drag', 'select · move'],
    ['Esc', 'deselect / cancel'],
    ['Del · ⌫', 'delete selection'],
    ['⌘/Ctrl + Z', 'undo'],
    ['⇧ ⌘/Ctrl + Z', 'redo'],
    ['⌘/Ctrl + D', 'duplicate'],
    ['R · ⇧R', 'rotate ±15°'],
    ['Arrows', 'nudge (⇧ = ×5)'],
    ['V', 'select tool'],
    ['G · F · B', 'grid · fit · bake'],
    ['⌘/Ctrl + S', 'export'],
  ];
  return (
    <div style={PANEL}>
      <h3 style={HEADING}>⌨ Shortcuts</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '5px 10px', alignItems: 'center' }}>
        {rows.map(([k, v]) => (
          <Fragment key={k}>
            <kbd style={KBD}>{k}</kbd>
            <span style={{ opacity: 0.8, fontSize: 12 }}>{v}</span>
          </Fragment>
        ))}
      </div>
    </div>
  );
}

// ── Validation panel ──────────────────────────────────────────────────────────────────────────────

export function ValidationPanel(props: {
  issues: readonly LevelIssue[];
  onSelectIssue: (targetId: string) => void;
}): JSX.Element {
  return (
    <div style={PANEL}>
      <h3 style={HEADING}>Validation ({props.issues.length})</h3>
      {props.issues.length === 0 ? (
        <p style={{ color: '#9be29b', margin: 0 }}>✓ No problems — this level would work.</p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflowY: 'auto' }}>
          {props.issues.map((iss, i) => (
            <li key={`${iss.code}-${iss.targetId ?? i}`}>
              <button
                style={{
                  ...BTN,
                  width: '100%',
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  borderColor: iss.level === 'error' ? 'rgba(255,143,143,0.6)' : 'rgba(255,210,74,0.5)',
                }}
                onClick={() => iss.targetId && props.onSelectIssue(iss.targetId)}
                disabled={!iss.targetId}
              >
                <span>{iss.level === 'error' ? '✖' : '⚠'}</span>
                <span style={{ flex: 1 }}>{iss.message}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
